import { app } from '@/firebase';

// Usage statistics (Google Analytics for Firebase), loaded only after the user
// opts in on the consent wall. Before that nothing is loaded and nothing is sent.
// Events are content-free: never pass prompt text, story text, titles or names.

// Bump when the privacy policy changes in a way users must accept again.
export const POLICY_VERSION = '2026-11';

const CONSENT_KEY = 'consent';
const MEASUREMENT_ID = app.options.measurementId;

let analyticsPromise = null;

function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getConsent() {
  const consent = readConsent();
  return consent && consent.policy === POLICY_VERSION ? consent : null;
}

// The Play app (Trusted Web Activity) opens the site with this referrer.
function detectPlatform() {
  try {
    if (document.referrer.startsWith('android-app://com.aimpulse.youstory')) {
      sessionStorage.setItem('platform', 'play');
    }
    return sessionStorage.getItem('platform') || 'web';
  } catch {
    return 'web';
  }
}

const platform = detectPlatform();

function start() {
  if (!MEASUREMENT_ID) return null;
  window[`ga-disable-${MEASUREMENT_ID}`] = false;
  if (analyticsPromise) {
    // Re-enabled after an opt-out in the same page load.
    analyticsPromise.then((a) => {
      if (a) a.mod.setAnalyticsCollectionEnabled(a.analytics, true);
    });
    return analyticsPromise;
  }
  analyticsPromise = import('firebase/analytics')
    .then(async (mod) => {
      if (!(await mod.isSupported())) return null;
      // Analytics only; no ads storage, signals or personalisation.
      mod.setConsent({
        analytics_storage: 'granted',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
      const analytics = mod.initializeAnalytics(app, {
        config: {
          allow_google_signals: false,
          allow_ad_personalization_signals: false,
        },
      });
      mod.setUserProperties(analytics, { app_platform: platform });
      return { mod, analytics };
    })
    .catch((e) => {
      console.warn('Analytics failed to start:', e?.message || e);
      return null;
    });
  return analyticsPromise;
}

function stop() {
  if (MEASUREMENT_ID) window[`ga-disable-${MEASUREMENT_ID}`] = true;
  if (analyticsPromise) {
    analyticsPromise.then((a) => {
      if (a) a.mod.setAnalyticsCollectionEnabled(a.analytics, false);
    });
  }
  // Remove the GA client-ID cookies so a later opt-in starts fresh.
  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0].trim();
    if (name === '_ga' || name.startsWith('_ga_')) {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${location.hostname}`;
      document.cookie = `${name}=; Max-Age=0; path=/`;
    }
  });
}

// Called once at app start: resumes statistics for users who already opted in.
export function initAnalytics() {
  const consent = getConsent();
  if (consent?.stats) start();
}

export function saveConsent(stats) {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ policy: POLICY_VERSION, stats, at: Date.now() })
    );
  } catch {
    // Storage blocked: consent holds for this page load only.
  }
  if (stats) {
    start();
    track('consent_accepted', { policy_version: POLICY_VERSION });
  } else {
    stop();
  }
}

export async function track(name, params = {}) {
  if (!getConsent()?.stats || !analyticsPromise) return;
  const a = await analyticsPromise;
  if (a) a.mod.logEvent(a.analytics, name, params);
}
